import { Shell } from '@/components/Shell';
import { BookingActions } from '@/components/bookings/BookingActions';
import { LicencePanel } from '@/components/bookings/LicencePanel';
import { Pill, stateLabel, stateToTone } from '@/components/ui/Pill';
import { adminApi } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

export const Route = createFileRoute('/bookings/$id')({
  beforeLoad: async () => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  component: BookingDetailPage,
});

function BookingDetailPage() {
  const { id } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'booking', id],
    queryFn: () => adminApi.bookingDetail(id),
  });
  const { data: paymentsData } = useQuery({
    queryKey: ['admin', 'booking', id, 'payments'],
    queryFn: () => adminApi.bookingPayments(id),
  });
  const dateFmt = new Intl.DateTimeFormat('pt-PT', { dateStyle: 'medium', timeStyle: 'short' });

  if (isLoading || !data) {
    return (
      <Shell page="…" sub="RESERVA · A CARREGAR">
        <div style={{ padding: 24, color: 'var(--lv-gray-500)' }}>A carregar…</div>
      </Shell>
    );
  }

  return (
    <Shell page={`#${data.code}`} sub="RESERVA · DETALHE">
      {/* State + meta header strip */}
      <div
        style={{
          background: '#fff',
          border: '1px solid var(--lv-line)',
          padding: '16px 24px',
          display: 'flex',
          gap: 24,
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <Pill label={stateLabel(data.state)} tone={stateToTone(data.state)} />
        <div style={{ fontSize: 12, color: 'var(--lv-gray-500)' }}>
          <span style={{ fontWeight: 700 }}>Canal:</span> {data.channel}
        </div>
        <div style={{ fontSize: 12, color: 'var(--lv-gray-500)' }}>
          <span style={{ fontWeight: 700 }}>Levantamento:</span> {dateFmt.format(new Date(data.fromAt))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--lv-gray-500)' }}>
          <span style={{ fontWeight: 700 }}>Devolução:</span> {dateFmt.format(new Date(data.toAt))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        {/* Detail card */}
        <section
          style={{
            background: '#fff',
            border: '1px solid var(--lv-line)',
            padding: 24,
          }}
        >
          <div className="label" style={{ color: 'var(--lv-brand)' }}>
            DETALHE
          </div>
          <div className="h-rule" style={{ marginTop: 8, marginBottom: 16 }} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Customer */}
            <InfoGroup label="Cliente">
              <div style={{ fontWeight: 700, fontSize: 14 }}>{data.customer.name ?? '—'}</div>
              <div style={{ fontSize: 12, color: 'var(--lv-gray-500)', marginTop: 2 }}>
                {data.customer.email}
              </div>
              {data.customer.phone && (
                <div style={{ fontSize: 12, color: 'var(--lv-gray-500)' }}>{data.customer.phone}</div>
              )}
            </InfoGroup>

            {/* Vehicle */}
            <InfoGroup label="Viatura">
              <div style={{ fontWeight: 700, fontSize: 14 }}>{data.vehicle.name}</div>
              <div
                style={{
                  fontSize: 12,
                  fontFamily: 'var(--lv-font-mono)',
                  color: 'var(--lv-gray-500)',
                  marginTop: 2,
                }}
              >
                {data.vehicle.plate}
              </div>
            </InfoGroup>

            {/* Pickup */}
            <InfoGroup label="Local de levantamento">
              <div style={{ fontWeight: 700, fontSize: 14 }}>{data.pickup?.name ?? '—'}</div>
              <div style={{ fontSize: 12, color: 'var(--lv-gray-500)', marginTop: 2 }}>
                {dateFmt.format(new Date(data.fromAt))}
              </div>
            </InfoGroup>

            {/* Return */}
            <InfoGroup label="Local de devolução">
              <div style={{ fontWeight: 700, fontSize: 14 }}>{data.returnLoc?.name ?? '—'}</div>
              <div style={{ fontSize: 12, color: 'var(--lv-gray-500)', marginTop: 2 }}>
                {dateFmt.format(new Date(data.toAt))}
              </div>
            </InfoGroup>
          </div>

          {(() => {
            const deposit = paymentsData?.items.find((p) => p.kind === 'deposit' && p.method === 'transfer');
            if (!deposit) return null;
            return (
              <div
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: '1px solid var(--lv-line)',
                }}
              >
                <div className="label" style={{ color: 'var(--lv-brand)', marginBottom: 8 }}>
                  COMPROVATIVO DE TRANSFERÊNCIA
                </div>
                {deposit.proofUrl ? (
                  <div
                    style={{
                      border: '1px solid var(--lv-line)',
                      padding: 12,
                      background: 'var(--lv-surface-soft)',
                      display: 'flex',
                      gap: 16,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ fontSize: 12 }}>
                      <div style={{ fontWeight: 700 }}>Ficheiro carregado pelo cliente</div>
                      {deposit.proofUploadedAt && (
                        <div style={{ color: 'var(--lv-gray-500)', marginTop: 2 }}>
                          {dateFmt.format(new Date(deposit.proofUploadedAt))}
                        </div>
                      )}
                    </div>
                    <a
                      href={deposit.proofUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        marginLeft: 'auto',
                        background: 'var(--lv-brand)',
                        color: '#fff',
                        padding: '8px 16px',
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        textDecoration: 'none',
                      }}
                    >
                      Abrir comprovativo
                    </a>
                  </div>
                ) : (
                  <p
                    style={{
                      fontSize: 12,
                      color: 'var(--lv-gray-500)',
                      padding: 12,
                      border: '1px dashed var(--lv-line)',
                      textAlign: 'center',
                    }}
                  >
                    Sem comprovativo. O cliente ainda não carregou nenhum ficheiro.
                  </p>
                )}
              </div>
            );
          })()}

          <LicencePanel customerId={data.customer.id} />

          {data.adminNotes && Object.values(data.adminNotes).some((v) => v) && (
            <div
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTop: '1px solid var(--lv-line)',
              }}
            >
              <div className="label" style={{ color: 'var(--lv-gray-500)', marginBottom: 8 }}>
                NOTAS INTERNAS
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--lv-ink)',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.55,
                  background: 'var(--lv-surface-soft)',
                  padding: '10px 14px',
                  border: '1px solid var(--lv-line)',
                }}
              >
                {data.adminNotes.pt || data.adminNotes.en || ''}
              </p>
            </div>
          )}
        </section>

        {/* Actions sidebar */}
        <div>
          <BookingActions
            bookingId={id}
            state={data.state}
            plate={data.vehicle.plate}
            depositPct={data.depositPct}
            balance={data.balance}
          />
        </div>
      </div>
    </Shell>
  );
}

function InfoGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: '14px 16px',
        border: '1px solid var(--lv-line)',
        background: 'var(--lv-surface-soft)',
      }}
    >
      <div className="label" style={{ color: 'var(--lv-gray-500)', marginBottom: 6 }}>
        {label}
      </div>
      {children}
    </div>
  );
}
